import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Unidade, WindxTela } from 'helper/windx';
import {
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxUnidade,
  selectWindxFiltrosCompletos,
  setUnidade,
  realizarConsulta,
} from 'redux/reducers/windxControlPanelReducer';
import SeletorEntidades from '../SeletorEntidades';
import BotaoDownload from '../BotaoDownload';
import SubdivisaoUnificada from './SubdivisaoUnificada';
import Unit from './Unit';

import './CabecalhoGrafico.scss';

interface CabecalhoGraficoProps {
  tela: Extract<WindxTela, 'sh' | 'sm'>;
  children: React.ReactNode;
  // Se `true`, o seletor sinaliza "nenhuma marcada = todas as entidades"
  // (visual + comportamento de payload no consumidor). Vale apenas para
  // Comparativo SM, onde o backend trata `id_entidades` ausente como
  // "todas". Em SH e Semanal o usuário precisa marcar ao menos uma.
  permiteSelecaoVazia?: boolean;
  // Se `false`, oculta o BotaoDownload (usado em Comparativo, que não tem ZIP).
  showDownload?: boolean;
}

/**
 * Card que envolve o gráfico/tabela das telas Weol-SH/SM/Comparativo. No
 * topo ficam os controles que segundo o Figma vivem dentro do card
 * (subdivisão à esquerda, resumo de entidades, pills de unidade e ícone
 * de download à direita) e abaixo o `children` (gráfico ou tabela).
 *
 * O seletor de entidades é apresentado como modal (FE-LAYOUT-003 — print
 * do Figma "Seleção Espacial → Conjuntos"). O modal abre automaticamente
 * quando o usuário escolhe / troca uma subdivisão espacial; também é
 * reabrível clicando no resumo.
 */
export default function CabecalhoGrafico({
  tela,
  children,
  permiteSelecaoVazia = false,
  showDownload = true,
}: CabecalhoGraficoProps) {
  const dispatch = useDispatch();
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const unidade = useSelector(selectWindxUnidade);
  const filtrosCompletos = useSelector(selectWindxFiltrosCompletos);

  const [showSeletor, setShowSeletor] = useState(false);
  // Guarda a última subdivisão observada para detectar mudança real
  // (evita re-abrir o modal a cada render).
  const subdivAnteriorRef = useRef<number | undefined>(idSubdivisao);

  useEffect(() => {
    if (idSubdivisao === undefined) return;
    if (subdivAnteriorRef.current !== idSubdivisao) {
      // Abre o modal apenas quando nao ha entidade selecionada. Ao restaurar
      // uma preferencia salva, setIdSubdivisaoEspacial zera idsEntidades no
      // mesmo batch em que setIdsEntidades(preferencia) e despachado — React
      // 18 consolida ambos antes do effect rodar, entao aqui idsEntidades ja
      // estara preenchido e o modal NAO abre.
      if (idsEntidades.length === 0) {
        setShowSeletor(true);
      }
      subdivAnteriorRef.current = idSubdivisao;
    }
  }, [idSubdivisao, idsEntidades]);

  return (
    <section className="windx-cabecalho-grafico">
      <header className="windx-cabecalho-grafico__topo">
        <div className="windx-cabecalho-grafico__esquerda">
          <SubdivisaoUnificada
            tela={tela}
            onAbrirSeletor={() => setShowSeletor(true)}
            permiteSelecaoVazia={permiteSelecaoVazia}
          />
        </div>
        <div className="windx-cabecalho-grafico__direita">
          <Unit
            valor={unidade as Unidade}
            onChange={(v) => {
              dispatch(setUnidade(v));
              // Auto-disparo so quando os obrigatorios estao todos preenchidos
              // (decisao da PO 2026-05-13: nada de request com painel incompleto).
              if (filtrosCompletos) {
                dispatch(realizarConsulta());
              }
            }}
          />
          {showDownload && <BotaoDownload tela={tela} iconOnly />}
        </div>
      </header>

      <div className="windx-cabecalho-grafico__corpo">
        {children}
      </div>

      <SeletorEntidades
        show={showSeletor}
        onHide={() => setShowSeletor(false)}
        permiteSelecaoVazia={permiteSelecaoVazia}
      />
    </section>
  );
}
